
import React, { useState } from 'react';
import Modal from './Modal';
import Button from '../ui/Button';
import FileUpload from '../ui/FileUpload';
import RichTextEditor from '../ui/RichTextEditor';

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AssignmentData) => void;
}

interface AssignmentData {
  title: string;
  description: string;
  attachments: File[];
  submissionTypes: string[];
  maxFileSize: number;
  dueDate: string;
  cutoffDate: string;
  maxMarks: number;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<AssignmentData>({
    title: '',
    description: '',
    attachments: [],
    submissionTypes: [],
    maxFileSize: 10,
    dueDate: '',
    cutoffDate: '',
    maxMarks: 100
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submissionTypeOptions = [
    { id: 'files', label: 'Files', icon: '📁' },
    { id: 'text', label: 'Text Input', icon: '📝' },
    { id: 'images', label: 'Images', icon: '🖼' },
    { id: 'videos', label: 'Videos', icon: '🎥' }
  ];

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    if (!formData.cutoffDate) {
      newErrors.cutoffDate = 'Cut-off date is required';
    }

    if (formData.dueDate && formData.cutoffDate && new Date(formData.cutoffDate) < new Date(formData.dueDate)) {
      newErrors.cutoffDate = 'Cut-off date must be after due date';
    }

    if (formData.maxMarks <= 0) {
      newErrors.maxMarks = 'Maximum marks must be greater than 0';
    }

    if (formData.submissionTypes.length === 0) {
      newErrors.submissionTypes = 'At least one submission type must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onSubmit(formData);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error creating assignment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      attachments: [],
      submissionTypes: [],
      maxFileSize: 10,
      dueDate: '',
      cutoffDate: '',
      maxMarks: 100
    });
    setErrors({});
  };

  const handleSubmissionTypeChange = (typeId: string) => {
    setFormData(prev => ({
      ...prev,
      submissionTypes: prev.submissionTypes.includes(typeId)
        ? prev.submissionTypes.filter(t => t !== typeId)
        : [...prev.submissionTypes, typeId]
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Assignment" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Assignment Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter assignment title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <RichTextEditor
            value={formData.description}
            onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
            placeholder="Enter assignment description and instructions..."
          />
          {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        </div>

        {/* File Attachments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assignment Files
          </label>
          <FileUpload
            onFilesSelected={(files) => setFormData(prev => ({ ...prev, attachments: files }))}
            label="Upload assignment materials"
          />
        </div>

        {/* Submission Types */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Accepted Submission Types *
          </label>
          <div className="grid grid-cols-2 gap-3">
            {submissionTypeOptions.map((option) => (
              <label key={option.id} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.submissionTypes.includes(option.id)}
                  onChange={() => handleSubmissionTypeChange(option.id)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-lg">{option.icon}</span>
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
          {errors.submissionTypes && <p className="mt-1 text-sm text-red-600">{errors.submissionTypes}</p>}
        </div>

        {/* File Size Limit */}
        <div>
          <label htmlFor="maxFileSize" className="block text-sm font-medium text-gray-700 mb-2">
            Maximum File Size (MB)
          </label>
          <input
            type="number"
            id="maxFileSize"
            min="1"
            max="100"
            value={formData.maxFileSize}
            onChange={(e) => setFormData(prev => ({ ...prev, maxFileSize: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
              📅 Due Date *
            </label>
            <input
              type="datetime-local"
              id="dueDate"
              value={formData.dueDate}
              onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.dueDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
          </div>

          <div>
            <label htmlFor="cutoffDate" className="block text-sm font-medium text-gray-700 mb-2">
              📅 Cut-off Date *
            </label>
            <input
              type="datetime-local"
              id="cutoffDate"
              value={formData.cutoffDate}
              onChange={(e) => setFormData(prev => ({ ...prev, cutoffDate: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.cutoffDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.cutoffDate && <p className="mt-1 text-sm text-red-600">{errors.cutoffDate}</p>}
          </div>
        </div>

        {/* Maximum Marks */}
        <div>
          <label htmlFor="maxMarks" className="block text-sm font-medium text-gray-700 mb-2">
            Maximum Marks
          </label>
          <input
            type="number"
            id="maxMarks"
            min="1"
            value={formData.maxMarks}
            onChange={(e) => setFormData(prev => ({ ...prev, maxMarks: parseInt(e.target.value) }))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.maxMarks ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.maxMarks && <p className="mt-1 text-sm text-red-600">{errors.maxMarks}</p>}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Creating...
              </span>
            ) : (
              'Create Assignment'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AssignmentModal;
